<template>
  <v-flex xs12>
    <v-card class="px-3 py-2">
      <v-flex xs12 px-3 py-2 v-if="err">
        <v-alert :value="true" type="error" v-html="err"></v-alert>
      </v-flex>
      <v-flex xs12 px-3 py-2 v-if="success">
        <v-alert :value="true" type="success">{{ success }}</v-alert>
      </v-flex>
      <v-form v-model="valid">
        <h3 class="pa-3">{{ $lang.labels.generalSettings }}</h3>
        <v-layout row wrap justify-space-between align-center>
          <v-flex xs12 md6 px-3 py-2 v-if="!isLocalAdmin">
            <v-autocomplete
              required
              :rules="[() => selectedPlanet.length > 0 || `${$lang.errors.missingPlanet}`]"
              :label="$lang.actions.selectPlanet"
              v-model="selectedPlanet"
              :items="mappedPlanets"
              :loading="planetLoading"
              :readonly="isEdit"
              :disabled="planets.length === 0"
            />
          </v-flex>
          <v-flex xs12 md6 px-3 py-2 v-if="!isLocalAdmin">
            <v-autocomplete
              required
              :rules="[() => selectedCompany.length > 0 || `${$lang.errors.missingCompany}`]"
              :label="$lang.actions.selectCompany"
              v-model="selectedCompany"
              :items="mappedCompanies"
              :loading="companyLoading"
              :readonly="isEdit"
              :disabled="companies.length === 0"
            />
          </v-flex>

          <v-flex xs12 md2 py-2 px-3 v-if="isEdit">
            <v-text-field
              :label="$lang.labels.externalIDres"
              v-model="externalResID"
              readonly
            />
          </v-flex>

          <v-flex xs12 :md4="isEdit" :md7="!isEdit" py-2 px-3>
            <v-text-field
              :label="$lang.labels.webshopUrl"
              v-model="webshopUrl"
              :rules="[(v) => validURL(v) || `${$lang.errors.notValidUrl}`, (v) => checkIsJustHostname(v) || `${$lang.errors.notValidUrl}`]"
            />
          </v-flex>

          <v-flex xs12 md3 px-3 py-2>
            <v-autocomplete
              multiple
              required
              :rules="[() => orderSubmissionAllowedFrom.length > 0 || `${$lang.errors.missingOrderSubmission}`]"
              :label="$lang.labels.orderSubmission"
              v-model="orderSubmissionAllowedFrom"
              :items="ordersFrom"
              :loading="orderSubmissionLoading"
              chips
              deletable-chips
            />
          </v-flex>
          <v-flex xs12 md3 px-3 py-2>
            <v-autocomplete
              required
              :rules="[() => selectedOrder.length > 0 || `${$lang.errors.missingOrder}`]"
              :label="$lang.actions.selectOrderType"
              v-model="selectedOrder"
              chips
              deletable-chips
              multiple
              :items="orderTypes"
            />
          </v-flex>
          <v-flex xs12 md4 px-3 py-2>
            <v-autocomplete
              required
              :rules="[() => selectedPayment.length > 0 || `${$lang.errors.missingPayment}`]"
              :label="$lang.actions.selectPayment"
              v-model="selectedPayment"
              chips
              deletable-chips
              multiple
              :items="payments"
            />
          </v-flex>
          <v-flex xs12 md2 px-3 pb-0 pt-2>
            <v-select
              required
              :disabled="selectedPayment.length === 0"
              :rules="[value => !!value || 'Required']"
              :label="$lang.labels.defaultPaymentType"
              v-model="defaultPaymentType"
              :items="selectedPayment.length === 0 ? [] : selectedPayment"
            />
          </v-flex>
          <v-flex xs12 md2 px-3>
            <span>{{ $lang.labels.webshopQr }}</span>
            <qrcode-vue v-if="webshopUrl && externalResID" :value="webshopQrCode" level="Q" style="margin-top: 4px"></qrcode-vue>
          </v-flex>
          <v-flex xs12 md2 px-3>
            <span>{{ $lang.labels.contactTrackingQr }}</span>
            <qrcode-vue v-if="webshopUrl" :value="contactTrackingQrCode" level="Q" style="margin-top: 4px"></qrcode-vue>
          </v-flex>
          <v-flex xs12 md2 px-3>
            <span>{{ $lang.labels.menuQr }}</span>
            <qrcode-vue v-if="webshopUrl" :value="menuQrCode" level="Q" style="margin-top: 4px"></qrcode-vue>
          </v-flex>
          <v-flex xs12 md6 px-3 py-2>
            <v-autocomplete
              required
              :rules="[() => selectedCatalogVersion !== null || `${$lang.errors.missingCatalogVersion}`]"
              :label="$lang.actions.selectCatalogVersion"
              v-model="selectedCatalogVersion"
              :items="mappedCatalogVersions"
              :disabled="catalogVersions.length === 0"
              :loading="catalogVersionsLoading"
            />
          </v-flex>
          <v-flex xs12 md6 px-3 py-2>
            <v-autocomplete
              required
              :rules="[() => selectedSubscription !== null || `${$lang.errors.missingSubscription}`]"
              :label="$lang.actions.selectSubscription"
              v-model="selectedSubscription"
              :items="subscriptions"
            />
          </v-flex>
          <v-flex xs12 md6 px-3 py-2>
            <v-autocomplete
              required
              :rules="[() => selectedCatalogView !== null || `${$lang.errors.missingCatalogView}`]"
              :label="$lang.actions.selectCatalogView"
              v-model="selectedCatalogView"
              :items="mappedCatalogViews"
              :disabled="catalogViews.length === 0"
              :loading="catalogViewsLoading"
            />
          </v-flex>
          <v-flex xs12 md6 px-3 py-2>
            <v-text-field
              required
              :rules="[() => name.length > 0 || `${$lang.errors.missingName}`]"
              :label="$lang.labels.name"
              v-model="name"
            />
          </v-flex>
          <v-flex xs12 md6 px-3 py-2>
            <v-text-field
              required
              :rules="[() => minDelivery != null || `${$lang.errors.missingDeliery}`]"
              :label="$lang.labels.minDelivery"
              v-model="minDelivery"
              type="number"
              min="0"
            />
          </v-flex>
          <v-layout row wrap>
            <v-flex xs12 md6 px-3 py-2>
              <v-text-field
                v-model="deliveryDurationFrom"
                :label="$lang.labels.deliveryDurationFrom"
                type="number"
                required
                :rules="[v => v >= 0 && v <= 120 || `${$lang.errors.timeError}`]"
                min="0"
                max="120"
              />
            </v-flex>
            <v-flex xs12 md6 px-3 py-2>
              <v-text-field
                v-model="deliveryDurationTo"
                :label="$lang.labels.deliveryDurationTo"
                type="number"
                required
                :rules="[v => v >= 0 && v <= 120 || `${$lang.errors.timeError}`]"
                min="0"
                max="120"
              />
            </v-flex>
          </v-layout>
        </v-layout>
        <v-layout row wrap>
          <v-flex xs12 md6 px-3 py-2>
            <v-checkbox
              hide-details
              class="mx-0"
              color="primary"
              :label="$lang.labels.sendToMoon"
              v-model="sendToMoon"
            />
            <v-checkbox
              hide-details
              class="mx-0"
              color="primary"
              :label="$lang.labels.notifyOwnerOnPaymentFailure"
              v-model="notifyOwnerOnPaymentFailure"
            />
            <v-checkbox
              hide-details
              class="mx-0"
              color="primary"
              :label="$lang.labels.acceptingOrders"
              v-model="acceptingOrders"
            />
          </v-flex>
          <v-flex xs12 md6 px-3 py-2>
            <v-checkbox
              hide-details
              class="mx-0"
              color="primary"
              :label="$lang.labels.showEmail"
              v-model="showEmail"
            />
            <v-checkbox
              hide-details
              class="mx-0"
              color="primary"
              :label="$lang.labels.showPhoneNumber"
              v-model="showPhoneNumber"
            />
          </v-flex>
        </v-layout>

        <hr class="my-3">
        <h3 class="pa-3">{{ $lang.labels.smsSettings }}</h3>

        <v-layout row wrap>
          <v-flex xs12 md6 px-3 py-2>
            <v-checkbox
              hide-details
              class="mx-0 pb-2"
              color="primary"
              :label="$lang.labels.smsConfirmationFirstOrder"
              v-model="smsConfirmationFirstOrder"
            />
            <v-text-field
              :placeholder="$lang.placeholders.smsUsername"
              :label="$lang.labels.smsUsername"
              v-model="smsUsername"
            />
          </v-flex>
          <v-flex xs12 px-3 pt-2>
            <span>{{ $lang.labels.passwordSmaResetPrefixText }}</span>
          </v-flex>
          <v-flex xs12 px-3>
            <v-btn
              :disabled="passwordSmsButtonDisabled"
              color="primary mx-0"
              @click="isSms = true; updatePassword = true"
            >
              {{ $lang.labels.updateSmsPassword }}
            </v-btn>
          </v-flex>
        </v-layout>

        <hr class="my-3">
        <h3 class="pa-3">{{ $lang.labels.discountSettings }}</h3>

        <v-flex xs12 px-3 py-2>
          <v-autocomplete
            :label="$lang.actions.selectRules"
            chips
            deletable-chips
            multiple
            :items="mappedDiscountRules"
            v-model="rules"
            :disabled="mappedDiscountRules.length === 0"
          />
        </v-flex>

        <hr class="my-3">

        <h3 class="pa-3">{{ $lang.header.emailConf }}</h3>
        <v-layout row wrap justify-space-between>
          <v-flex xs12 md6 px-3 py-2>
            <v-text-field
              required
              :placeholder="$lang.placeholders.emailId"
              :rules="[() => sendEmail.length > 0 || `${$lang.errors.missingEmailID}`]"
              :label="$lang.labels.sendEmail"
              v-model="sendEmail"
            />
          </v-flex>
          <v-flex xs12 md6 px-3 py-2>
            <v-textarea
              auto-grow
              rows="1"
              :placeholder="$lang.placeholders.bccEmail"
              :label="$lang.labels.sendBCC"
              v-model="bccEmail"
            />
          </v-flex>
          <v-flex xs12 md6 px-3>
            <v-textarea
              auto-grow
              rows="3"
              :label="$lang.labels.prefix"
              v-model="prefix"
            />
          </v-flex>
          <v-flex xs12 md6 px-3>
            <v-textarea
              auto-grow
              rows="3"
              :label="$lang.labels.postfix"
              v-model="postfix"
            />
          </v-flex>
          <hr class="my-3">
          <v-flex xs12>
            <h3 class="pa-3">{{ $lang.header.paymentGatewaySettings }}</h3>
          </v-flex>
          <v-flex xs12 md6 px-3 v-if="isEdit && webshopStatus !== 'created'">
            <v-select
              :label="$lang.labels.payGateway"
              :items="gateways"
              v-model="selectedGateway"
            />
          </v-flex>
          <v-flex xs12 md12 px-3 v-if="isEdit && webshopStatus === 'created'">
            <span>{{ $lang.labels.activateShopFirst }}</span>
          </v-flex>
        </v-layout>
        <v-layout row wrap justify-space-between align-center
                  v-if="['paymentsense', 'firstdata', 'connect-e', 'tecs'].includes(selectedGateway) && webshopStatus !== 'created'">
          <v-flex xs12 md6 px-3 v-if="selectedGateway === 'paymentsense' || selectedGateway === 'tecs'">
            <v-text-field
              :label="$lang.labels.merchantId"
              v-model="merchantId"
              :rules="[(v) => !!v || `${$lang.errors.missingMerchantId}`]"
              required
            />
          </v-flex>
          <v-flex xs12 md6 px-3 v-if="selectedGateway === 'paymentsense'">
            <v-text-field
              :label="$lang.labels.preSharedKey"
              v-model="preSharedKey"
              :rules="[(v) => !!v || `${$lang.errors.missingPreSharedKey}`]"
              required
            />
          </v-flex>
          <v-flex xs12 v-if="selectedGateway === 'firstdata'">
            <v-flex xs12 md6 px-3>
              <v-text-field
                :label="$lang.labels.storeId"
                v-model="firstDataStoreId"
                :rules="[(v) => !!v || `${$lang.errors.missingStoreId}`]"
                required
              />
            </v-flex>
          </v-flex>
          <v-flex xs12 md6 px-3 v-if="selectedGateway === 'connect-e'">
            <v-text-field
              :label="$lang.labels.connectEApiKey"
              v-model="connectEApiKey"
              :rules="[(v) => !!v || `${$lang.errors.missingApiKey}`]"
              required
            />
          </v-flex>
          <v-flex xs12 md6 px-3 v-if="selectedGateway === 'connect-e'">
            <v-text-field
              :label="$lang.labels.connectEUsername"
              v-model="connectEUsername"
              :rules="[(v) => !!v || `${$lang.errors.missingUsernameP}`]"
              required
            />
          </v-flex>
          <template>
            <v-flex xs12 px-3 pt-2>
              <span>{{ $lang.labels.passwordResetPrefixText }}</span>
            </v-flex>
            <v-flex xs12 px-3>
              <v-btn
                :disabled="passwordButtonDisabled"
                color="primary mx-0"
                @click="updatePassword = true"
              >
                {{ $lang.labels.updatePassword }}
              </v-btn>
            </v-flex>
          </template>
        </v-layout>

        <hr class="my-3">

        <v-layout row wrap justify-space-between align-center>
          <h3 class="pa-3">{{ $lang.header.htmlHints }}</h3>
          <v-flex xs12 px-3>
            <v-btn
              color="primary mx-0"
              @click="addHtmlCode()"
            >
              {{ $lang.labels.addHtml }}
            </v-btn>
            <v-btn
              color="primary mx-0"
              @click="htmlImportModal = true"
            >
              {{ $lang.labels.importHtml }}
            </v-btn>
          </v-flex>
          <v-flex xs12 px-3 pb-3>
            <v-data-table
              v-if="htmlHints"
              :headers="headers"
              :items="htmlHints"
              hide-actions
              class="elevation-0 mt-2">
              <template slot="items" slot-scope="props">
                <td>{{ props.item.name }}</td>
                <td v-if="props.item.name !== 'webshop.logo' && props.item.name !== 'webshop.headerImage.mobile'">
                  {{ props.item.value }}
                </td>
                <td v-else>
                  <v-img :src="props.item.value" contain max-height="60"></v-img>
                </td>
                <td>
                  <v-btn
                    class="primary mx-0"
                    @click="editHtmlCode(props.item)"
                  >
                    {{ $lang.actions.edit }}
                  </v-btn>
                  <v-btn
                    class="error mx-0"
                    @click="confirmDeleteHtmlCode(props.item)"
                  >
                    {{ $lang.actions.delete }}
                  </v-btn>
                </td>
              </template>
            </v-data-table>
          </v-flex>
        </v-layout>

        <template v-if="isEdit">
          <hr class="my-3">
          <v-layout row wrap justify-space-between align-center>
            <v-flex xs12 sm6 px-3>
              <v-text-field
                :label="$lang.labels.created"
                type="text"
                v-model="createdOn"
              />
            </v-flex>
            <v-flex xs12 sm6 px-3>
              <v-text-field
                :label="$lang.labels.modified"
                type="text"
                v-model="modifiedOn"
              />
            </v-flex>
          </v-layout>
        </template>
        <v-flex xs12 px-3 py-2 v-if="err">
          <v-alert :value="true" type="error" v-html="err"></v-alert>
        </v-flex>
        <v-flex xs12 px-3 py-2 v-if="success">
          <v-alert :value="true" type="success">{{ success }}</v-alert>
        </v-flex>
        <v-layout row wrap justify-space-between align-center py-2>
          <v-btn
            flat class="ml-0"
            @click="$router.push({ name: 'webShop' })">
            <v-icon>keyboard_arrow_left</v-icon>
            {{ $lang.actions.close }}
          </v-btn>
          <v-btn
            class="primary mx-0"
            :disabled="!valid"
            @click="submit">
            {{ $lang.actions.submit }}
          </v-btn>
        </v-layout>
      </v-form>
    </v-card>
    <v-snackbar
      v-model="snackbar"
      bottom
      :timeout="snackbarTimeout"
      color="error"
      multi-line>
      {{ $lang.labels.sendToMoonWarning }}
      <v-btn
        flat
        @click="snackbar = false"
      >
        {{ $lang.actions.close }}
      </v-btn>
    </v-snackbar>
    <v-dialog persistent v-if="activationModal" v-model="activationModal" max-width="500">
      <activation-modal :data="activationData" @close="redirectBack()"/>
    </v-dialog>
    <v-dialog persistent v-if="updatePassword" v-model="updatePassword" max-width="500">
      <change-password
        @close="updatePassword = false; isSms = false;" :isSms="isSms"
      />
    </v-dialog>
    <v-dialog persistent v-if="confirmationModal" v-model="confirmationModal" max-width="500">
      <confirmation-modal :text="$lang.actions.deleteHtmlHint" @submit="deleteHtmlCode()"
                          @close="confirmationModal = false; tempIndex = null"/>
    </v-dialog>
    <v-dialog persistent v-if="htmlModal" v-model="htmlModal" max-width="1200">
      <html-hints-modal :htmlCode="htmlCodeToEdit" :isHtmlEdit="isHtmlEdit" :htmlKeysListFiltered="htmlKeysListFiltered"
                        @submit="saveHtmlCode($event)"
                        @close="htmlModal = false, htmlCodeToEdit = null, tempIndex = null"/>
    </v-dialog>
    <v-dialog persistent v-if="htmlImportModal" v-model="htmlImportModal" max-width="1200">
      <html-hints-import-modal @submit="importHtmlCode($event)" @close="htmlImportModal = false"/>
    </v-dialog>
  </v-flex>
</template>

<script>
import {DateTime} from 'luxon';
import Cookie from "vue-cookie";
import QrcodeVue from 'qrcode.vue'

export default {
  components: {
    activationModal: () => import('../modals/WebshopActivationModal'),
    changePassword: () => import('../modals/ChangePassword'),
    confirmationModal: () => import('../modals/ConfirmationModal'),
    htmlHintsModal: () => import('../modals/HtmlHintsModal'),
    htmlHintsImportModal: () => import('../modals/HtmlHintsImportModal'),
    QrcodeVue,
  },
  data: () => ({
    isEdit: false,
    err: '',
    valid: false,
    discLoading: false,
    sendToMoon: false,
    companyLoading: true,
    planetLoading: true,
    catalogVersionsLoading: false,
    catalogViewsLoading: false,
    success: '',
    selectedPlanet: '',
    selectedCompany: '',
    selectedPayment: '',
    selectedOrder: '',
    selectedCatalogVersion: null,
    selectedSubscription: null,
    selectedCatalogView: null,
    webshopStatus: null,
    name: '',
    minDelivery: null,
    planets: [],
    companies: [],
    payments: ['card', 'cash'],
    orderTypes: [],
    catalogVersions: [],
    subscriptions: [],
    catalogViews: [],
    rules: [],
    discountRules: [],
    sendEmail: '',
    bccEmail: '',
    prefix: '',
    postfix: '',
    externalResID: '',
    gateways: [],
    selectedGateway: '',
    merchantId: '',
    connectEApiKey: '',
    connectEUsername: '',
    preSharedKey: '',
    firstDataStoreId: '',
    firstDataSharedSecret: '',
    gateway: false,
    firstPassword: false,
    updatePassword: false,
    orderSubmissionAllowedFrom: [],
    ordersFrom: [],
    orderSubmissionLoading: true,
    activationModal: false,
    activationData: null,
    snackbar: false,
    snackbarTimeout: 0,
    createdOn: undefined,
    modifiedOn: undefined,
    deliveryDurationTo: 0,
    deliveryDurationFrom: 45,
    htmlHints: null,
    isHtmlEdit: false,
    htmlModal: false,
    htmlCodeToEdit: null,
    confirmationModal: false,
    tempIndex: null,
    htmlKeysList: [
      'webshop.logo',
      'webshop.headerImage.mobile',
      'webshop.page1.prefix.de',
      'webshop.page1.postfix.de',
      'webshop.page2.prefix.de',
      'webshop.page2.postfix.de',
      'webshop.page1.prefix.en',
      'webshop.page1.postfix.en',
      'webshop.page2.prefix.en',
      'webshop.page2.postfix.en',
      'webshop.footer.copyright-html',
      'webshop.footer.privacy-and-cookies-policy-link',
      'webshop.footer.termins-and-conditions-link',
      'webshop.partner.company-name',
      'webshop.partner.vat-number',
      'webshop.partner.registration-number',
      'webshop.partner.postall-address',
      'webshop.partner.email-address',
      'webshop.deactivation.de',
      'webshop.deactivation.en',
    ],
    headers: [
      {
        text: 'Name',
        value: 'name',
        align: 'left',
        sortable: true,
        width: '20%',
      },
      {
        text: 'Value',
        value: 'value',
        align: 'left',
        sortable: true,
        width: '60%',
      },
      {
        text: 'Actions',
        value: 'actions',
        align: 'left',
        sortable: false,
        width: '20%',
      },
    ],
    htmlImportModal: false,
    acceptingOrders: true,
    defaultPaymentType: '',
    notifyOwnerOnPaymentFailure: true,
    webshopUrl: '',
    merchantIdBe: null,
    preSharedKeyBe: null,
    firstDataStoreIdBe: null,
    connectEApiKeyBe: null,
    connectEUsernameBe: null,
    showEmail: true,
    showPhoneNumber: true,
    smsConfirmationFirstOrder: false,
    smsUsername: '',
    smsUsernameBe: '',
    isSms: false,
    isLocalAdmin: false,
  }),
  watch: {
    selectedCompany(val) {
      this.fetchCatalogVersions(val);
      this.fetchDiscountRulesForCompany(val);
    },
    selectedCatalogVersion(val) {
      this.fetchCatalogViews(val);
    },
    err(val) {
      if (val !== '') {
        setTimeout(() => {
          this.err = '';
        }, 5000);
      }
    },
    selectedPayment(val) {
      if (!val.includes(this.defaultPaymentType)) this.defaultPaymentType = '';
    }
  },
  computed: {
    webshopQrCode() {
      const urlArr = this.webshopUrl.split('.');
      if (urlArr.includes('onmapos')) return `${this.webshopUrl}/id/${this.externalResID}`;
      if (urlArr.includes('acepos')) return `${this.webshopUrl}/id/${this.externalResID}`;
      return this.webshopUrl;
    },
    contactTrackingQrCode() {
      return `${this.webshopUrl}/contact-tracking/${this.externalResID}`;
    },
    menuQrCode() {
      return `${this.webshopUrl}/menu?webShopResourceId=${this.externalResID}`;
    },
    htmlKeysListFiltered() {
      if (this.htmlHints) {
        const tempHtmlHintsKeys = this.htmlHints.map(item => item.name);
        return this.htmlKeysList.filter(item => !tempHtmlHintsKeys.includes(item));
      }
      return this.htmlKeysList;
    },
    mappedCatalogVersions() {
      return this.catalogVersions.sort((a, b) => (Number(a.catalogVersion) < Number(b.catalogVersion)) ? 1 : -1).map(version => ({
        text: `${version.catalogVersion} ${version.catalogName ? ` - ${version.catalogName}` : ''} (${version.status})`,
        value: version.catalogId,
      }));
    },
    mappedCatalogViews() {
      return this.catalogViews.map(view => ({
        text: `${view.viewId} - ${view.name.en}`,
        value: view.viewId,
      }));
    },
    mappedDiscountRules() {
      return this.discountRules.map(rule => ({
        text: `${rule.ruleName} (${rule.status})`,
        value: rule.id,
      }));
    },
    mappedPlanets() {
      return this.planets.map(planet => ({
        text: `${planet.name} (${planet.status})`,
        value: planet.instanceId,
      }));
    },
    mappedCompanies() {
      return this.companies.map(company => ({
        text: `${company.name} (${company.companyStatus})`,
        value: company.id,
      }));
    },
    passwordButtonDisabled() {
      if (this.selectedGateway === 'paymentsense') {
        return !(this.merchantIdBe && this.preSharedKeyBe && this.merchantIdBe === this.merchantId && this.preSharedKeyBe === this.preSharedKey);
      }
      if (this.selectedGateway === 'firstdata') {
        return !(this.firstDataStoreIdBe && this.firstDataStoreIdBe === this.firstDataStoreId);
      }
      if (this.selectedGateway === 'tecs') {
        return !(this.merchantIdBe && this.merchantIdBe === this.merchantId);
      }
      if (this.selectedGateway === 'connect-e') {
        return !(this.connectEApiKeyBe && this.connectEUsernameBe && this.connectEApiKeyBe === this.connectEApiKey && this.connectEUsernameBe === this.connectEUsername);
      }
      return true;
    },
    passwordSmsButtonDisabled() {
      return !(this.smsConfirmationFirstOrder && !!this.smsUsernameBe);
    },
  },
  methods: {
    validURL(str) {
      const pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
      return !!pattern.test(str);
    },
    checkIsJustHostname(url) {
      const a = document.createElement('a');
      a.href = url;

      if (a.pathname !== '/') return false;
      if (a.search) return false;
      if (a.hash) return false;
      if (url.substr(url.length - 1) === '/') return false;
      return true;
    },
    formatDate(date) {
      return DateTime.fromISO(date).toFormat('yyyy-MM-dd HH:mm:ss');
    },
    redirectBack() {
      this.activationModal = false;
      this.$router.push({name: 'webShop'});
    },
    fetchDiscountRulesForCompany(id) {
      this.discLoading = true;
      this.discountRules = [];
      this.$http.post('discountrule/getall', {companyId: id})
        .then((res) => {
          if (res.body.status === 'Success') {
            this.discLoading = false;
            this.discountRules = res.body.discountRules;
            if (this.discountRules.lenght === 0) {
              this.discLoading = false;
            }
            return;
          }
          this.err = res.body.statusText;
        }, (err) => {
          this.discLoading = false;
          this.err = err.body.statusText || err.body.message;
        });
    },
    fetchCatalogVersions(companyId) {
      this.catalogVersionsLoading = true;
      this.catalogVersions = [];
      this.$http.post('catalog/getcatalogversions', {companyId})
        .then((res) => {
          this.catalogVersionsLoading = false;
          if (res.body.status === 'Success') {
            this.catalogVersions = res.body.details;
            return;
          }
          this.err = res.body.statusText;
        }, (err) => {
          this.catalogVersionsLoading = false;
          this.err = err.body.statusText || err.body.message;
        });
    },
    fetchCatalogViews(id) {
      this.catalogViewsLoading = true;
      this.catalogViews = [];
      this.$http.get(`catalog/getwebshopviews/${id}`)
        .then((res) => {
          this.catalogViewsLoading = false;
          if (res.body.status === 'Success') {
            this.catalogViews = res.body.details;
            return;
          }
          this.err = res.body.statusText;
        }, (err) => {
          this.catalogViewsLoading = false;
          this.err = err.body.statusText || err.body.message;
        });
    },
    init() {
      return new Promise((resolve) => {
        const roles = JSON.parse(this.$cookie.get('roles') || '[]');
        this.isLocalAdmin = roles.includes('ROLE_SUN_LOCAL_ADMIN');
        Promise.all([
          this.$http.get('company')
            .then((res) => {
              this.companyLoading = false;
              if (res.body.status === 'Success') {
                this.companies = res.body.companies;
                if (this.isLocalAdmin && this.companies.length === 1) this.selectedCompany = this.companies[0].id;
                return;
              }
              this.err = res.body.statusText;
            }, (err) => {
              this.companyLoading = false;
              this.err = err.body.statusText || err.body.message;
            }),
          this.$http.get('system/webshop/fetchsubscriptionmodels')
            .then((res) => {
              if (res.body.status === 'Success') {
                this.subscriptions = res.body.subscriptionModels;
                return;
              }
              this.err = res.body.statusText;
            }, (err) => {
              this.err = err.body.statusText || err.body.message;
            }),
          this.isLocalAdmin ? true : this.$http.get('planetinstances/getallfortype/webshop')
              .then((res) => {
                this.planetLoading = false;
                if (res.body.status === 'Success') {
                  this.planets = res.body.details;
                  return;
                }
                this.err = res.body.statusText;
              }, (err) => {
                this.planetLoading = false;
                this.err = err.body.statusText || err.body.message;
              }),
          this.$http.get('system/webshop/getordersubmissionallowedfrom')
            .then((res) => {
              this.orderSubmissionLoading = false;
              if (res.body.status === 'Success') {
                this.ordersFrom = res.body.orderSubmissionAllowedFrom;
                return;
              }
              this.err = res.body.statusText;
            }, (err) => {
              this.orderSubmissionLoading = false;
              this.err = err.body.statusText || err.body.message;
            }),
          this.$http.get('system/ordertypes')
            .then((res) => {
              this.orderSubmissionLoading = false;
              if (res.body.status === 'Success') {
                this.orderTypes = res.body.webshop;
                return;
              }
              this.orderTypes = ['collection', 'delivery'];
              this.err = res.body.statusText;
            }, (err) => {
              this.orderSubmissionLoading = false;
              this.orderTypes = ['collection', 'delivery'];
              this.err = err.body.statusText || err.body.message;
            }),
        ]).then(() => resolve());
      });
    },
    timeout: ms => new Promise(resolve => setTimeout(resolve, ms)),
    async waitForFinish(val) {
      await this.timeout(100);
      if (this[val]) {
        return this.waitForFinish(val);
      }
    },
    checkHtmlHints() {
      if (!this.htmlHints) return this.$lang.errors.missingAllHint;
      const tempHtmlHints = this.htmlHints.map(hint => hint.name);
      let errors = '';
      const mandatoryHtmlHints = [
        'webshop.partner.company-name',
        'webshop.partner.vat-number',
        'webshop.partner.registration-number',
        'webshop.partner.postall-address',
        'webshop.partner.email-address',
      ];
      for (const hint of mandatoryHtmlHints) {
        if (!tempHtmlHints.includes(hint)) {
          errors = errors + `${this.$lang.errors.missingHint}: '${hint}'` + '<br>';
        }
      }
      this.htmlHints.forEach(hint => {
        if (hint.value === '') {
          errors = errors + `'${hint.name}' ${this.$lang.errors.emptyValue}` + '<br>';
        }
      })
      return errors;
    },
    async submit() {
      const selectedVersion = this.catalogVersions.find(catalog => catalog.catalogId === this.selectedCatalogVersion);
      const areHtmlHintsOk = await this.checkHtmlHints();
      if (areHtmlHintsOk !== '') {
        this.err = areHtmlHintsOk;
        return;
      }
      const data = {
        name: this.name,
        planetInstanceId: this.selectedPlanet,
        email: this.sendEmail,
        subscriptionModel: this.selectedSubscription,
        catalogViewId: this.selectedCatalogView,
        catalogVersion: selectedVersion.catalogVersion,
        paymentTypes: this.selectedPayment,
        orderTypes: this.selectedOrder,
        sendToMoon: this.sendToMoon,
        minDeliveryAmount: this.minDelivery,
        rules: this.rules,
        confirmationEmailPrefix: this.prefix || null,
        confirmationEmailPostFix: this.postfix || null,
        bccEmail: this.bccEmail || null,
        orderSubmissionAllowedFrom: this.orderSubmissionAllowedFrom,
        deliveryDurationTo: this.deliveryDurationTo,
        deliveryDurationFrom: this.deliveryDurationFrom,
        htmlHints: this.htmlHints,
        acceptingOrders: this.acceptingOrders,
        defaultPaymentType: this.defaultPaymentType,
        notifyOwnerOnPaymentFailure: this.notifyOwnerOnPaymentFailure,
        webshopUrl: this.webshopUrl,
        showEmail: this.showEmail,
        showPhoneNumber: this.showPhoneNumber,
        smsConfirmationFirstOrder: this.smsConfirmationFirstOrder,
        smsPassword: '',
      };
      if (this.smsUsername) data.smsUsername = this.smsUsername;
      if (!this.isEdit) {
        data.companyId = this.selectedCompany;
      } else if (this.selectedGateway) {
        data.paymentGateway = this.selectedGateway;
        if (this.selectedGateway === 'paymentsense') {
          data.merchantId = this.merchantId;
          data.preSharedKey = this.preSharedKey;
        } else if (this.selectedGateway === 'firstdata') {
          data.firstDataStoreId = this.firstDataStoreId;
        } else if (this.selectedGateway === 'tecs') {
          data.merchantId = this.merchantId;
        } else if (this.selectedGateway === 'connect-e') {
          data.connectEApiKey = this.connectEApiKey;
          data.connectEUsername = this.connectEUsername;
        }
      }
      const url = this.isEdit ? `webshopresource/update/${this.$route.params.id}` : 'webshopresource/create';
      this.$http.post(url, data)
        .then((res) => {
          if (res.body.status === 'Success') {
            this.success = this.isEdit ? 'Webshop updated successfully.' : 'Webshop created successfully.';
            console.log(data.sendToMoon);
            if (data.sendToMoon === true) {
              this.snackbar = true;
            }
            if (!this.isEdit) {
              this.activationModal = true;
              this.activationData = res.body.webShopResourceId;
            }
            return null;
          }
          this.err = res.body.statusText;
          return null;
        })
        .then((res) => {
          if (res === null) return;
          if (res.body.status === 'Success') {
            this.success = 'Webshop updated successfully.';
            if (data.sendToMoon === true) {
              this.snackbar = true;
            }
            return;
          }
          this.err = res.body.statusText;
        })
        .catch((err) => {
          this.err = err.body.statusText || err.body.message;
        });
    },
    addHtmlCode() {
      this.isHtmlEdit = false;
      this.htmlModal = true;
    },
    saveHtmlCode(item) {
      if (this.tempIndex !== null) {
        this.htmlHints[this.tempIndex] = item;
      } else {
        if (!this.htmlHints) {
          this.htmlHints = []
        }
        this.htmlHints.push(item);
      }
      this.tempIndex = null;
      this.htmlCodeToEdit = null;
      this.isHtmlEdit = false;
      this.htmlModal = false;
    },
    editHtmlCode(item) {
      this.tempIndex = this.htmlHints.indexOf(item);
      this.htmlCodeToEdit = item;
      this.isHtmlEdit = true;
      this.htmlModal = true;
    },
    confirmDeleteHtmlCode(item) {
      this.tempIndex = this.htmlHints.indexOf(item);
      this.confirmationModal = true;
    },
    deleteHtmlCode() {
      this.confirmationModal = false;
      this.htmlHints.splice(this.tempIndex, 1);
      this.tempIndex = null;
    },
    importHtmlCode(htmlCode) {
      this.htmlImportModal = false;
      if (this.htmlHints && this.htmlHints.length > 0) {
        const newNames = htmlCode.map(x => x.name);
        for (let i = 0, l = this.htmlHints.length; i < l; i++) {
          for (let j = 0, ll = htmlCode.length; j < ll; j++) {
            const oldNames = this.htmlHints.map(x => x.name);
            if (!oldNames.includes(newNames[j])) {
              this.htmlHints.push(htmlCode[j]);
            } else {
              if (this.htmlHints[i].name === htmlCode[j].name) {
                this.htmlHints.splice(i, 1, htmlCode[j]);
                break;
              }
            }
          }
        }
      } else {
        this.htmlHints = htmlCode;
      }
    },
  },
  created() {
    this.gateways = JSON.parse(localStorage.getItem('paymentGateways'));
    this.init()
      .then(() => {
        if (this.$route.params.id) {
          let webshop;
          this.$http.get(`webshopresource/${this.$route.params.id}`)
            .then((res) => {
              return new Promise((resolve) => {
                if (res.body.status === 'Success') {
                  this.isEdit = true;
                  webshop = res.body.webShopResource;
                  this.acceptingOrders = webshop.acceptingOrders;
                  this.webshopStatus = webshop.status;
                  this.selectedCompany = webshop.companyId;
                  this.selectedPlanet = webshop.planetInstanceId;
                  this.selectedPayment = webshop.paymentTypes;
                  this.selectedOrder = webshop.orderTypes;
                  this.selectedSubscription = webshop.subscriptionModel;
                  this.name = webshop.name;
                  this.minDelivery = webshop.minDeliveryAmount;
                  this.sendToMoon = webshop.sendToMoon;
                  this.externalResID = webshop.webShopResourceExternalId;
                  this.sendEmail = webshop.email;
                  this.bccEmail = webshop.confirmationEmailSettingsDetails ? webshop.confirmationEmailSettingsDetails.bccEmail : null;
                  this.prefix = webshop.confirmationEmailSettingsDetails ? webshop.confirmationEmailSettingsDetails.prefix : null;
                  this.postfix = webshop.confirmationEmailSettingsDetails ? webshop.confirmationEmailSettingsDetails.postfix : null;
                  this.orderSubmissionAllowedFrom = webshop.orderSubmissionAllowedFrom;
                  this.deliveryDurationTo = webshop.deliveryDurationTo;
                  this.deliveryDurationFrom = webshop.deliveryDurationFrom;
                  this.htmlHints = webshop.htmlHints;
                  this.notifyOwnerOnPaymentFailure = webshop.notifyOwnerOnPaymentFailure;
                  this.defaultPaymentType = webshop.defaultPaymentType || '';
                  this.webshopUrl = webshop.webshopUrl || '';
                  this.showEmail = webshop.showEmail;
                  this.showPhoneNumber = webshop.showPhoneNumber;
                  this.smsConfirmationFirstOrder = webshop.smsConfirmationFirstOrder;
                  this.smsUsername = webshop.smsUsername || '';
                  this.smsUsernameBe = webshop.smsUsername || '';
                  if (webshop.createdOn) {
                    this.createdOn = this.formatDate(webshop.createdOn);
                  }
                  if (webshop.modifiedOn) {
                    this.modifiedOn = this.formatDate(webshop.modifiedOn);
                  }
                  this.catalogVersionsLoading = true;
                  if (webshop.rules !== null) {
                    this.rules = webshop.rules;
                  }
                  if (webshop.paymentGatewaySettingDetails) {
                    this.selectedGateway = webshop.paymentGatewaySettingDetails.paymentGateway;
                    this.merchantId = webshop.paymentGatewaySettingDetails.merchantId;
                    this.preSharedKey = webshop.paymentGatewaySettingDetails.preSharedKey;
                    this.firstDataStoreId = webshop.paymentGatewaySettingDetails.firstDataStoreId;
                    this.firstDataSharedSecret = webshop.paymentGatewaySettingDetails.firstDataSharedSecret;
                    this.connectEApiKey = webshop.paymentGatewaySettingDetails.connectEApiKey;
                    this.connectEUsername = webshop.paymentGatewaySettingDetails.connectEUsername;
                    this.password = webshop.paymentGatewaySettingDetails.password;
                    this.merchantIdBe = webshop.paymentGatewaySettingDetails.merchantId;
                    this.preSharedKeyBe = webshop.paymentGatewaySettingDetails.preSharedKey;
                    this.firstDataStoreIdBe = webshop.paymentGatewaySettingDetails.firstDataStoreId;
                    this.connectEApiKeyBe = webshop.paymentGatewaySettingDetails.connectEApiKey;
                    this.connectEUsernameBe = webshop.paymentGatewaySettingDetails.connectEUsername;
                    if (!this.password) {
                      this.firstPassword = true;
                    }
                  } else {
                    this.firstPassword = true;
                  }
                  this.waitForFinish('catalogVersionsLoading')
                    .then(() => resolve());
                  return;
                }
                this.err = res.body.statusText;
                resolve();
              });
            })
            .then(() => {
              if (!webshop) return;
              const selectedVersion = this.catalogVersions.find(catalog => `${catalog.catalogVersion}` === `${webshop.catalogVersion}`);
              this.selectedCatalogVersion = `${selectedVersion.catalogId}`;
              this.selectedCatalogView = webshop.catalogViewId;
            })
            .catch((err) => {
              this.err = err.body.statusText || err.body.message;
            })
        } else {
          this.htmlHints = [
            {name: 'webshop.partner.company-name', value: ''},
            {name: 'webshop.partner.vat-number', value: ''},
            {name: 'webshop.partner.registration-number', value: ''},
            {name: 'webshop.partner.postall-address', value: ''},
            {name: 'webshop.partner.email-address', value: ''},
          ];
        }
      });
  },
}
</script>



// WEBPACK FOOTER //
// WebShopDetail.vue?40bdf5f0